import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Banner, DataTable, EmptyState, StatusChip, TopAppBar } from '@meshble/molo';
import { api, must, type Job } from '../lib/api';
import { useIstanze, useServers } from '../lib/dati';
import { durata, quandoRelativo, titolo } from '../lib/attivita';
import { BarraUtente } from '../shell';
import { History } from '../icons';
import { CaricoSezione } from '../brand';
import { t } from '../lib/lingua';

export const TONO: Record<Job['status'], 'neutral' | 'running' | 'error' | 'success'> = {
  queued: 'neutral',
  running: 'running',
  succeeded: 'success',
  failed: 'error',
  cancelled: 'neutral',
};
export const ETICHETTA: Record<Job['status'], string> = {
  queued: 'In coda',
  running: 'In corso',
  succeeded: 'Completata',
  failed: 'Fallita',
  cancelled: 'Annullata',
};

/** Quanto è durata, dai due istanti; niente se non è ancora finita. */
function quantoDura(j: Job): string {
  if (!j.startedAt) return '—';
  const fine = j.finishedAt ? new Date(j.finishedAt).getTime() : Date.now();
  return durata((fine - new Date(j.startedAt).getTime()) / 1000);
}

/**
 * Le attività: tutto ciò che l'hub fa per conto dell'organizzazione e che
 * dura più di un clic. Ogni riga dice cosa, su cosa, com'è andata e quando;
 * aprendola si vede passo per passo.
 */
export function JobsPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/jobs' });
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['jobs', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/jobs', { params: { path: { slug } } })),
    refetchInterval: (query) =>
      (query.state.data?.jobs ?? []).some((j) => j.status === 'running' || j.status === 'queued') ? 5000 : false,
  });
  const jobs = q.data?.jobs ?? [];

  // Il job porta gli id: i nomi si cercano nelle due liste, che sono già in
  // cache per chiunque abbia aperto una schermata prima di questa.
  const servers = useServers(slug);
  const istanze = useIstanze(slug);
  const nomeServer = new Map((servers.data?.servers ?? []).map((s) => [s.id, s.name]));
  const nomeIstanza = new Map((istanze.data?.instances ?? []).map((i) => [i.id, i.name]));
  // Un'attività su un'istanza prende il nome dell'istanza, e se quella non
  // c'è più — l'ha rimossa proprio questa attività — resta senza nome: il
  // nome del server al suo posto direbbe che è stato rimosso il server.
  const bersaglio = (j: Job) =>
    j.kind.endsWith('_instance')
      ? (j.instanceId && nomeIstanza.get(j.instanceId)) || undefined
      : (j.serverId && nomeServer.get(j.serverId)) || undefined;

  return (
    <>
      <TopAppBar title={t('Attività')} actions={<BarraUtente slug={slug} />} />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        {q.error && <Banner tone="error" title={t('Non riesco a leggere le attività')}>{q.error.message}</Banner>}
        {q.isSuccess && jobs.length === 0 && (
          <EmptyState icon={<History />} title={t('Ancora nessuna attività')}>
            {t('Qui trovi tutto quello che Molo fa per te e che dura più di un momento: installazioni, nuove istanze, backup, ripristini. Ne comparirà una appena colleghi un server.')}
          </EmptyState>
        )}
        {jobs.length > 0 && (
          <DataTable
            columns={[
              { key: 'cosa', header: t('Attività') },
              { key: 'stato', header: t('Stato') },
              { key: 'quando', header: t('Avviata'), nowrap: true },
              { key: 'durata', header: t('Durata'), numeric: true, nowrap: true },
            ]}
            rowKey={(r) => String(r.id)}
            onRowClick={(r) => navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: String(r.id) } })}
            rows={jobs.map((j) => ({
              id: j.id,
              cosa: (
                <div className="md-due-righe">
                  <span className="md-body-large">{titolo(j.kind, bersaglio(j))}</span>
                  {j.status === 'failed' && j.error && <span className="md-clamp">{j.error}</span>}
                </div>
              ),
              stato: <StatusChip status={j.status} label={t(ETICHETTA[j.status])} tone={TONO[j.status]} />,
              quando: quandoRelativo(j.startedAt ?? j.createdAt),
              durata: quantoDura(j),
            }))}
          />
        )}
      </div>
    </>
  );
}
